import useSafeAsyncEffect from '@column/lib/frontend/hooks/useSafeAsyncEffect';
import { safeAsync, safeGetOrThrow } from '@column/lib/safeWrappers';
import { ERef } from '@column/lib/types';
import { Ad } from '@column/lib/types/ad';
import { OrderFilingType } from '@column/lib/types/filingType';
import { MadlibDataType } from '@column/lib/types/madlib';
import { wrapError, wrapSuccess } from '@column/lib/types/responses';
import {
  columnObjectsAreEqual,
  safeStringify
} from '@column/lib/utils/stringify';
import { useEffect, useState } from 'react';
import { fetchMadlibsConfig } from 'routes/madlib/helpers/config';
import { getBooleanFlag } from 'utils/flags';
import { LaunchDarklyFlags } from '@column/lib/types/launchDarklyFlags';
import { logInfo } from 'utils/logger';
import { AdEditorData } from '../../steps/DraftContent/FormattedEditorWrapper';
import { getInitialMadlibData } from './prefillMadlibs';

const DEFAULT_MADLIB_DATA: MadlibDataType = {
  templateData: {},
  questionTemplateData: {}
};

type UseMadlibsProps<T extends Ad> = {
  filingType: ERef<OrderFilingType>;
  adData: Partial<T>;
  onChange: (update: AdEditorData) => void;
};

export const useMadlibs = <T extends Ad>({
  filingType,
  adData,
  onChange
}: UseMadlibsProps<T>) => {
  const [allQuestionsAnswered, setAllQuestionsAnswered] = useState(false);
  const [renderedHtml, setRenderedHtml] = useState(adData.content || '');
  const [madlibData, setMadlibData] = useState(
    adData.madlibData ?? DEFAULT_MADLIB_DATA
  );
  const [validationResults, setValidationResults] = useState<
    Record<string, boolean>
  >({});
  const [madlibIsComplete, setMadlibIsComplete] = useState(false);

  const {
    value: madlibProps,
    isError: madlibPropsError,
    isLoading: madlibPropsLoading
  } = useSafeAsyncEffect({
    fetchData: async () => {
      const { response: filingTypeSnap, error: filiingTypeError } =
        await safeGetOrThrow(filingType);
      if (filiingTypeError) {
        return wrapError(filiingTypeError);
      }

      const madlibConfigPath = filingTypeSnap.data().madlib;
      if (!madlibConfigPath) {
        return wrapSuccess(null);
      }

      const {
        response: templateAndQuestions,
        error: templateAndQuestionsError
      } = await safeAsync(() => fetchMadlibsConfig(madlibConfigPath))();
      if (templateAndQuestionsError) {
        return wrapError(templateAndQuestionsError);
      }

      const {
        template: madlibTemplate,
        questions,
        questionConfig
      } = templateAndQuestions;
      const configFileMissing =
        !!madlibConfigPath && !(madlibTemplate && questions);
      if (configFileMissing) {
        return wrapError(
          new Error(
            'Madlib template or questions missing for order filing type'
          )
        );
      }

      const shouldPrefillData = columnObjectsAreEqual(
        madlibData,
        DEFAULT_MADLIB_DATA
      );
      if (shouldPrefillData) {
        const defaultMadlibData = getInitialMadlibData(adData, questions);
        if (defaultMadlibData) {
          setMadlibData(defaultMadlibData);
        }
      }

      return wrapSuccess({
        madlibConfigPath,
        madlibTemplate,
        questions,
        questionConfig
      });
    },
    dependencies: [filingType?.id]
  });

  /**
   * TODO(web): I think eventually we should be able to remove this useEffect, but that will
   * likely require some refactoring of the MadlibEditor, which I don't want to do while that component
   * is shared by the notice & order placemnt flows. When we migrate notices over to the orders schema, we
   * can consolidate simplify the code in this hook and that component.
   */
  useEffect(() => {
    /**
     * These checks prevent us from setting blank madlib data on non-madlib obits & classifieds unnecessarily
     * (this has no known adverse side effects, but could get confusing to someone looking at the data)
     */
    const madlibDataIsEmptyAndUnedited =
      columnObjectsAreEqual(madlibData, DEFAULT_MADLIB_DATA) &&
      !adData.madlibData;
    const contentIsEmptyAndUnedited = !renderedHtml && !adData.content;

    // Skip content updates when full content editor is shown - let the editor manage content directly
    const shouldSkipContentUpdate =
      madlibProps?.questionConfig?.showFullContentEditor;

    onChange({
      ...(!shouldSkipContentUpdate && !contentIsEmptyAndUnedited
        ? { content: renderedHtml }
        : {}),
      ...(madlibDataIsEmptyAndUnedited ? {} : { madlibData })
    });

    // NOTE: do not extend this pattern further. If we do this for more madlibs, we should build a pattern
    // to handle this in a more generic way.
    const enableAutoSettingDeathNoticeTitles = getBooleanFlag(
      LaunchDarklyFlags.ENABLE_AUTO_SETTING_TITLES_ON_DEATH_NOTICES
    );
    const deathNoticeDeceasedName =
      madlibData?.questionTemplateData?.ad__deceasedName;
    if (
      enableAutoSettingDeathNoticeTitles &&
      deathNoticeDeceasedName &&
      typeof deathNoticeDeceasedName === 'string' &&
      deathNoticeDeceasedName !== adData?.title
    ) {
      logInfo('Auto-setting death notice title', {
        deathNoticeDeceasedName,
        adDataTitle: adData.title
      });
      onChange({
        title: deathNoticeDeceasedName
      });
    }
  }, [safeStringify(madlibData), renderedHtml]);

  useEffect(() => {
    if (
      madlibData.templateData &&
      Object.keys(madlibData.templateData).length
    ) {
      const noInvalidData = Object.entries(madlibData.templateData).every(
        ([key, input]) => !!input && validationResults[key] !== false
      );
      setMadlibIsComplete(noInvalidData && allQuestionsAnswered);
    }
  }, [safeStringify(madlibData), safeStringify(validationResults)]);

  return {
    madlibProps,
    madlibPropsError,
    madlibPropsLoading,
    allQuestionsAnswered,
    madlibData,
    madlibIsComplete,
    setAllQuestionsAnswered,
    setValidationResults,
    setRenderedHtml,
    setMadlibData
  };
};
